import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Admin.css';

interface SmtpForm {
  host: string;
  port: number;
  username: string;
  password: string;
  use_ssl: boolean;
  from_email: string;
  from_name: string;
}

const PRESETS = [
  {
    key: 'yandex',
    label: 'Яндекс',
    icon: 'envelope-fill',
    host: 'smtp.yandex.ru',
    port: 587,
    use_ssl: false,
    hint: 'Используйте пароль приложения из настроек Яндекс ID → Безопасность → Пароли приложений и режим STARTTLS на порту 587',
  },
  {
    key: 'google',
    label: 'Google (Gmail)',
    icon: 'google',
    host: 'smtp.gmail.com',
    port: 587,
    use_ssl: false,
    hint: 'Включите двухфакторную аутентификацию, затем создайте пароль приложения в настройках Google-аккаунта',
  },
  {
    key: 'mailru',
    label: 'Mail.ru',
    icon: 'envelope',
    host: 'smtp.mail.ru',
    port: 465,
    use_ssl: true,
    hint: 'Создайте пароль приложения в настройках Mail.ru → Безопасность',
  },
  {
    key: 'rambler',
    label: 'Rambler',
    icon: 'envelope-at',
    host: 'smtp.rambler.ru',
    port: 465,
    use_ssl: true,
    hint: 'Используйте пароль приложения из настроек Rambler',
  },
  {
    key: 'custom',
    label: 'Другой',
    icon: 'gear',
    host: '',
    port: 587,
    use_ssl: false,
    hint: 'Введите настройки вашего SMTP-сервера вручную',
  },
] as const;

const defaultForm: SmtpForm = {
  host: '',
  port: 465,
  username: '',
  password: '',
  use_ssl: true,
  from_email: '',
  from_name: '',
};

const SmtpSettings: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [notice, setNotice] = useState('');
  const [noticeOk, setNoticeOk] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [form, setForm] = useState<SmtpForm>(defaultForm);

  useEffect(() => {
    api.get('/api/settings/smtp')
      .then(r => {
        const data = r.data as SmtpForm;
        setForm({ ...defaultForm, ...data, password: '' });
        // Detect which preset matches
        const match = PRESETS.find(p => p.host && p.host === data.host);
        setSelectedPreset(match ? match.key : (data.host ? 'custom' : ''));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const applyPreset = (preset: typeof PRESETS[number]) => {
    setSelectedPreset(preset.key);
    if (preset.key !== 'custom') {
      setForm(f => ({ ...f, host: preset.host, port: preset.port, use_ssl: preset.use_ssl }));
    }
  };

  const showMsg = (msg: string, ok: boolean) => {
    setNotice(msg);
    setNoticeOk(ok);
    setTimeout(() => setNotice(''), 5000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/settings/smtp', form);
      showMsg('Настройки SMTP сохранены', true);
    } catch (e: any) {
      showMsg(e?.response?.data?.detail || 'Ошибка сохранения', false);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!form.host) { showMsg('Заполните SMTP-сервер', false); return; }
    setTesting(true);
    try {
      const r = await api.post('/api/settings/smtp/test', form);
      showMsg((r.data as any)?.message || 'Тест успешен', true);
    } catch (e: any) {
      showMsg(e?.response?.data?.detail || 'Ошибка при тестировании', false);
    } finally {
      setTesting(false);
    }
  };

  const activeHint = PRESETS.find(p => p.key === selectedPreset)?.hint || '';

  const inp: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div className="admin-page">
      <div className="smtp-page-inner">

        {/* Header */}
        <div className="admin-header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => navigate('/dashboard')} className="header-btn" title="На главную">
              <i className="bi bi-arrow-left"></i>
            </button>
            <h2 style={{ margin: 0, fontSize: 20 }}>
              <i className="bi bi-envelope" style={{ marginRight: 8, color: '#6366f1' }}></i>
              Настройки SMTP
            </h2>
          </div>
          <button onClick={logout} className="header-btn" title="Выход">
            <i className="bi bi-box-arrow-right"></i>
          </button>
        </div>

        {/* Notice */}
        {notice && (
          <div style={{
            background: noticeOk ? '#ecfeff' : '#fef2f2',
            border: `1px solid ${noticeOk ? '#a5f3fc' : '#fca5a5'}`,
            color: noticeOk ? '#155e75' : '#dc2626',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <i className={`bi bi-${noticeOk ? 'check-circle' : 'exclamation-triangle'}`}></i>
            {notice}
          </div>
        )}

        {loading ? (
          <div style={{ color: '#6b7280', padding: 20 }}>Загрузка...</div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>

            {/* Presets */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Быстрая настройка провайдера
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {PRESETS.map(p => (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p)}
                    className="header-btn"
                    style={{
                      padding: '7px 16px',
                      fontSize: 13,
                      fontWeight: selectedPreset === p.key ? 600 : 400,
                      background: selectedPreset === p.key ? '#e0e7ff' : undefined,
                      border: selectedPreset === p.key ? '1px solid #6366f1' : undefined,
                      color: selectedPreset === p.key ? '#4338ca' : undefined,
                    }}
                  >
                    <i className={`bi bi-${p.icon}`} style={{ marginRight: 5 }}></i>
                    {p.label}
                  </button>
                ))}
              </div>
              {activeHint && (
                <div style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#0369a1',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 7,
                }}>
                  <i className="bi bi-info-circle" style={{ marginTop: 1, flexShrink: 0 }}></i>
                  {activeHint}
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', marginBottom: 20 }} />

            {/* Host + Port */}
            <div className="smtp-grid-row">
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>SMTP-сервер (хост) *</label>
                <input
                  style={inp}
                  value={form.host}
                  onChange={e => setForm({ ...form, host: e.target.value })}
                  placeholder="smtp.yandex.ru"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Порт *</label>
                <input
                  style={inp}
                  type="number"
                  value={form.port}
                  onChange={e => setForm({ ...form, port: parseInt(e.target.value) || 465 })}
                />
              </div>
            </div>

            {/* Username */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Логин (email-адрес) *</label>
              <input
                style={inp}
                type="email"
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="user@yandex.ru"
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Пароль (пароль приложения) *</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  style={{ ...inp, flex: 1 }}
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Введите новый пароль или оставьте пустым"
                  autoComplete="new-password"
                />
                <button
                  className="header-btn"
                  onClick={() => setShowPassword(v => !v)}
                  title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  style={{ padding: '8px 11px', flexShrink: 0 }}
                >
                  <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                Оставьте пустым, чтобы не менять сохранённый пароль
              </div>
            </div>

            {/* SSL toggle */}
            <div style={{ marginBottom: 14 }}>
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: '8px 12px',
                background: form.use_ssl ? '#f0fdf4' : '#fafafa',
                border: `1px solid ${form.use_ssl ? '#86efac' : '#e5e7eb'}`,
                borderRadius: 8,
              }}>
                <input
                  type="checkbox"
                  checked={form.use_ssl}
                  onChange={e => setForm({ ...form, use_ssl: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontSize: 14 }}>
                  <i className={`bi bi-shield${form.use_ssl ? '-lock-fill' : '-x'}`} style={{ marginRight: 5, color: form.use_ssl ? '#16a34a' : '#9ca3af' }}></i>
                  {form.use_ssl ? 'SSL/TLS включён (порт 465)' : 'STARTTLS (порт 587)'}
                </span>
              </label>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', marginBottom: 20 }} />

            {/* From email / From name */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Email отправителя (From)</label>
                <input
                  style={inp}
                  type="email"
                  value={form.from_email}
                  onChange={e => setForm({ ...form, from_email: e.target.value })}
                  placeholder="support@example.com"
                />
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Если пусто — берётся логин</div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Имя отправителя</label>
                <input
                  style={inp}
                  value={form.from_name}
                  onChange={e => setForm({ ...form, from_name: e.target.value })}
                  placeholder="Портал поддержки"
                />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="logout-btn"
                onClick={handleSave}
                disabled={saving}
                style={{ width: 'auto', padding: '9px 20px' }}
              >
                <i className="bi bi-floppy" style={{ marginRight: 6 }}></i>
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                className="header-btn"
                onClick={handleTest}
                disabled={testing || !form.host}
                style={{ padding: '9px 20px' }}
              >
                <i className="bi bi-send" style={{ marginRight: 6 }}></i>
                {testing ? 'Отправка...' : 'Отправить тестовое письмо'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmtpSettings;
